import { Avatar, Box, Flex, HStack, Image, Text } from "@chakra-ui/react";
import { FeedCardActions } from "./FeedCardActions";
import { FeedCardHalo } from "./FeedCardHalo";
import { FeedFeaturedCreatorItem } from "./types";

interface FeedFeaturedCreatorCardProps {
  item: FeedFeaturedCreatorItem;
  isActive: boolean;
}

/**
 * "Discover <creator>" card. Image-forward: a row of large comic covers
 * fills the body of the card, with a creator strip (avatar + name) sitting
 * above. Tapping the strip goes to the creator page; tapping a cover goes
 * to that comic.
 */
export function FeedFeaturedCreatorCard({
  item,
  isActive,
}: FeedFeaturedCreatorCardProps) {
  // Halo color from the first comic cover so the glow matches the
  // creator's lead work.
  const haloSrc =
    item.comics[0]?.cover_url ?? item.comics[0]?.cover_small_url ?? null;
  return (
    <Box position="relative" w="100%" h="100%">
      <FeedCardHalo src={haloSrc} />
    <Box
      position="relative"
      zIndex={1}
      w="100%"
      h="100%"
      bgGradient="linear(135deg, rgba(28, 50, 60, 0.95) 0%, rgba(14, 18, 24, 0.95) 100%)"
      borderRadius="2xl"
      border="1px solid rgba(255,255,255,0.06)"
      overflow="hidden"
      color="white"
    >
    <Flex direction="column" w="100%" h="100%">
      <Flex
        as="a"
        href={item.artist_url}
        tabIndex={isActive ? 0 : -1}
        align="center"
        gap={3}
        px={4}
        py={3}
        flexShrink={0}
        cursor="pointer"
        _hover={{ opacity: 0.85 }}
        transition="opacity 120ms ease"
      >
        <Avatar
          size="md"
          src={item.avatar_url ?? undefined}
          name={item.artist_name}
          bg="cyan.500"
        />
        <Flex direction="column" flex="1" minW="0">
          {/* "Discover" tag promoted to the section header above the card. */}
          <Text fontSize="lg" fontWeight="bold" lineHeight="1.2" noOfLines={1}>
            {item.artist_name}
          </Text>
        </Flex>
      </Flex>
      <HStack
        spacing={2}
        px={3}
        pb={3}
        flex="1"
        minH="0"
        align="stretch"
      >
        {item.comics.map((comic) => (
          <Box
            as="a"
            key={comic.comic_id}
            href={comic.url}
            tabIndex={isActive ? 0 : -1}
            position="relative"
            flex="1"
            minW="0"
            borderRadius="md"
            overflow="hidden"
            bg="black"
            cursor="pointer"
            _hover={{ filter: "brightness(1.1)" }}
            transition="filter 120ms ease"
          >
            {comic.cover_url ?? comic.cover_small_url ? (
              <Image
                src={comic.cover_url ?? comic.cover_small_url ?? ""}
                alt={comic.name}
                w="100%"
                h="100%"
                objectFit="cover"
              />
            ) : (
              <Box
                w="100%"
                h="100%"
                bgGradient="linear(135deg, cyan.400 0%, blue.700 100%)"
              />
            )}
            {/* Bottom gradient + title so the cover doubles as a labeled
                button without needing a separate text row. */}
            <Box
              position="absolute"
              left={0}
              right={0}
              bottom={0}
              h="50%"
              bgGradient="linear(to-t, rgba(0,0,0,0.85) 0%, transparent 100%)"
              pointerEvents="none"
            />
            <Text
              position="absolute"
              left={2}
              right={2}
              bottom={2}
              fontSize="xs"
              fontWeight="bold"
              noOfLines={2}
              lineHeight="1.15"
              sx={{ textShadow: "0 1px 4px rgba(0,0,0,0.7)" }}
            >
              {comic.name}
            </Text>
          </Box>
        ))}
      </HStack>
    </Flex>
    {/* Floats above the cover row in the bottom-right corner — same spot
        as every other card's actions for consistency. */}
    <Box position="absolute" bottom={3} right={3} zIndex={2}>
      <FeedCardActions isActive={isActive} />
    </Box>
    </Box>
    </Box>
  );
}
